import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  FormControl,
  CircularProgress,
  Box,
  Typography,
  FormHelperText,
  TextField,
  IconButton,
  Popper,
  Paper,
  Tooltip,
  ClickAwayListener,
  InputAdornment,
  Skeleton,
  Fade,
  Chip,
  SwipeableDrawer,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import SyncIcon from '@mui/icons-material/Sync';
import CheckIcon from '@mui/icons-material/Check';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import { LocalTemplate, MetaTemplate, VerifyResult } from '../../types/templates';
import { templatesApi } from '../../services/api';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface TemplateSelectorBaseProps {
  smbId: string;
  value: string;
  label?: string;
  required?: boolean;
  unavailableTemplateIds?: string[];
  error?: boolean;
  helperText?: React.ReactNode;
  templateVerifyError?: VerifyResult;
  disabled?: boolean;
  onFocus?: () => void;
  enableFilters?: boolean;
}

export interface LocalSelectorProps extends TemplateSelectorBaseProps {
  type?: 'local';
  onSelect: (template: LocalTemplate | null) => void;
  templates?: LocalTemplate[];
}

export interface MetaSelectorProps extends TemplateSelectorBaseProps {
  type: 'meta';
  onSelect: (template: MetaTemplate | null) => void;
  templates?: MetaTemplate[];
}

export type TemplateSelectorProps = LocalSelectorProps | MetaSelectorProps;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

type AnyTemplate = LocalTemplate | MetaTemplate;

const getLocalId = (t: AnyTemplate) => (t as LocalTemplate).id;
const getMetaId = (t: AnyTemplate) => (t as MetaTemplate).platform_template_id;
const getLocalStatus = (t: AnyTemplate) => (t as LocalTemplate).template_status;
const getMetaStatus = (t: AnyTemplate) => (t as MetaTemplate).status;

// ---------------------------------------------------------------------------
// Skeleton rows for loading state
// ---------------------------------------------------------------------------

const DropdownSkeleton = () => (
  <Box sx={{ p: 1 }}>
    {[1, 2, 3, 4].map((i) => (
      <Box
        key={i}
        sx={{
          px: 2,
          py: 1.25,
          display: 'flex',
          flexDirection: 'column',
          gap: 0.5,
        }}
      >
        <Skeleton
          variant="text"
          width={`${50 + Math.random() * 35}%`}
          height={18}
          animation="wave"
          sx={{ borderRadius: 0.5 }}
        />
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Skeleton variant="rounded" width={36} height={12} animation="wave" sx={{ borderRadius: 4 }} />
          <Skeleton variant="rounded" width={52} height={12} animation="wave" sx={{ borderRadius: 4 }} />
          <Skeleton variant="rounded" width={56} height={12} animation="wave" sx={{ borderRadius: 4 }} />
        </Box>
      </Box>
    ))}
  </Box>
);

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const TemplateSelector: React.FC<TemplateSelectorProps> = (props) => {
  const {
    type = 'local',
    smbId,
    value,
    label = type === 'local' ? 'Select a Template' : 'Select Meta Template',
    required = false,
    unavailableTemplateIds = [],
    error = false,
    helperText = '',
    templateVerifyError = null,
    disabled = false,
    onFocus,
    enableFilters = true,
  } = props;

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const isLocal = type === 'local';
  const getId = isLocal ? getLocalId : getMetaId;
  const getStatus = isLocal ? getLocalStatus : getMetaStatus;

  const externalTemplates = isLocal
    ? (props as LocalSelectorProps).templates
    : (props as MetaSelectorProps).templates;
  const hasExternalTemplates = Array.isArray(externalTemplates);

  // ===================== DATA LAYER =====================

  const [fetchedData, setFetchedData] = useState<AnyTemplate[]>([]);
  const [loading, setLoading] = useState(!hasExternalTemplates);
  const [hasFetched, setHasFetched] = useState(false);
  const isMountedRef = useRef(true);

  const [statusFilter, setStatusFilter] = useState<string[]>(['APPROVED']);
  const [availableStatuses, setAvailableStatuses] = useState<string[]>([]);
  const sortedStatuses = useMemo(() => [...availableStatuses].sort(), [availableStatuses]);

  const [verifying, setVerifying] = useState(false);
  const [verifyResult, setVerifyResult] = useState<VerifyResult | null>(templateVerifyError ?? null);

  useEffect(() => { setVerifyResult(templateVerifyError ?? null); }, [templateVerifyError]);
  useEffect(() => { setVerifyResult(null); }, [value]);
  useEffect(() => { isMountedRef.current = true; return () => { isMountedRef.current = false; }; }, []);

  const allTemplates = hasExternalTemplates
    ? (externalTemplates as AnyTemplate[])
    : fetchedData;

  useEffect(() => {
    if (!isLocal) return;
    const s = new Set<string>();
    allTemplates.forEach(t => { const st = getStatus(t); if (st) s.add(st.toUpperCase()); });
    setAvailableStatuses(Array.from(s));
  }, [allTemplates, isLocal, getStatus]);

  const loadTemplates = useCallback(async () => {
    if (hasExternalTemplates || !smbId) return;
    setLoading(true);
    try {
      if (isLocal) {
        const res = await templatesApi.getTemplates({
          smb_id: smbId,
          limit: 100,
        });
        if (isMountedRef.current) {
          setFetchedData(res.templates || []);
          if (res.available_statuses) setAvailableStatuses(res.available_statuses);
        }
      } else {
        const res = await templatesApi.getMetaTemplates(smbId);
        if (isMountedRef.current) setFetchedData(res.templates || []);
      }
    } catch (err) {
      console.error('Failed to fetch templates:', err);
    } finally {
      if (isMountedRef.current) { setLoading(false); setHasFetched(true); }
    }
  }, [smbId, isLocal, hasExternalTemplates]);

  useEffect(() => { loadTemplates(); }, [loadTemplates]);

  const templatesToRender = useMemo(() => {
    let list: AnyTemplate[] = allTemplates;
    if (!isLocal && unavailableTemplateIds.length > 0) {
      list = list.filter(t => !unavailableTemplateIds.includes(getMetaId(t)));
    }
    if (enableFilters && isLocal && statusFilter.length > 0) {
      const lower = statusFilter.map(s => s.toLowerCase());
      list = list.filter(t => { const s = getStatus(t); return s && lower.includes(s.toLowerCase()); });
    }
    return list;
  }, [allTemplates, isLocal, enableFilters, statusFilter, unavailableTemplateIds, getStatus]);

  const valueObj = useMemo(() => {
    if (!value) return null;
    return allTemplates.find(t => getId(t) === value) ?? null;
  }, [allTemplates, getId, value]);

  // ===================== UI STATE =====================

  const instanceIdRef = useRef(Math.random().toString(36).substr(2, 9));
  const [open, setOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const anchorRef = useRef<HTMLDivElement>(null);
  const mainInputRef = useRef<HTMLInputElement>(null);
  const mobileSearchRef = useRef<HTMLInputElement>(null);

  // Close this selector if another template selector opens anywhere on the page
  useEffect(() => {
    if (!open) return;
    const handleGlobalSelectorOpen = (e: Event) => {
      const customEvt = e as CustomEvent;
      if (customEvt.detail?.instanceId !== instanceIdRef.current) {
        setOpen(false);
        setSearchText('');
      }
    };
    window.addEventListener('template-selector-open', handleGlobalSelectorOpen);
    return () => {
      window.removeEventListener('template-selector-open', handleGlobalSelectorOpen);
    };
  }, [open]);

  const visibleTemplates = useMemo(() => {
    let list = templatesToRender;
    if (searchText.trim()) {
      const q = searchText.toLowerCase();
      list = list.filter(t =>
        t.name.toLowerCase().includes(q) ||
        t.category?.toLowerCase().includes(q) ||
        t.language?.toLowerCase().includes(q)
      );
    }
    // Pin currently selected template to the top of the list
    if (value) {
      list = [...list].sort((a, b) => {
        const aSelected = getId(a) === value;
        const bSelected = getId(b) === value;
        if (aSelected && !bSelected) return -1;
        if (!aSelected && bSelected) return 1;
        return 0;
      });
    }
    return list;
  }, [templatesToRender, searchText, value, getId]);

  // ===================== HANDLERS =====================

  const handleOpen = useCallback(() => {
    if (disabled) return;
    // Broadcast to close all other open template selectors on the page
    window.dispatchEvent(new CustomEvent('template-selector-open', { detail: { instanceId: instanceIdRef.current } }));
    setSearchText('');
    setOpen(true);
    onFocus?.();
    setTimeout(() => {
      if (isMobile) {
        mobileSearchRef.current?.focus();
      } else {
        mainInputRef.current?.focus();
      }
    }, 80);
  }, [disabled, isMobile, onFocus]);

  const handleClose = useCallback(() => {
    setSearchText('');
    setOpen(false);
  }, []);

  const handleSelect = useCallback((template: AnyTemplate) => {
    if (isLocal) {
      const tid = getLocalId(template);
      if (unavailableTemplateIds.includes(tid) && tid !== value) return;
      (props as LocalSelectorProps).onSelect(template as LocalTemplate);
    } else {
      (props as MetaSelectorProps).onSelect(template as MetaTemplate);
    }
    setSearchText('');
    setOpen(false);
  }, [isLocal, props, unavailableTemplateIds, value]);

  const handleClear = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (open && searchText) {
      setSearchText('');
      return;
    }
    if (isLocal) (props as LocalSelectorProps).onSelect(null);
    else (props as MetaSelectorProps).onSelect(null);
    setSearchText('');
    setOpen(false);
  }, [isLocal, open, props, searchText]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Escape') { handleClose(); }
  }, [handleClose]);

  const handleClickAway = useCallback(() => {
    if (isMobile) return;
    handleClose();
  }, [handleClose, isMobile]);

  const handleToggle = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (disabled) return;
    if (open) { handleClose(); }
    else { handleOpen(); }
  }, [disabled, open, handleClose, handleOpen]);

  const handleFilterToggle = useCallback((status: string) => {
    if (status === 'ALL') {
      setStatusFilter([]);
      return;
    }
    // Multi-select status filtering: toggle status in/out of selection array
    setStatusFilter(prev =>
      prev.includes(status) ? prev.filter(s => s !== status) : [...prev, status]
    );
  }, []);

  const handleVerify = useCallback(async () => {
    if (!value || !smbId || !isLocal) return;
    const selectedTpl = allTemplates.find(t => getLocalId(t) === value) as LocalTemplate | undefined;
    if (!selectedTpl?.platform_template_id) return;
    setVerifying(true);
    setVerifyResult(null);
    try {
      const res = await templatesApi.verifyTemplateStatus(smbId, [selectedTpl.platform_template_id]);
      const result = res[selectedTpl.platform_template_id];
      if (result) { setVerifyResult(result); loadTemplates(); }
    } catch {
      setVerifyResult({ can_proceed: false, status: 'error', message: 'Failed to verify template. Please try again.' });
    } finally {
      setVerifying(false);
    }
  }, [value, smbId, isLocal, allTemplates, loadTemplates]);

  // ===================== RENDER COMPONENTS =====================

  const verifyMessage = verifyResult?.message;
  const verifyColor = verifyResult?.can_proceed ? 'success.main' : 'error.main';
  const isLoading = loading && !hasExternalTemplates;

  // Filter Pills Bar (Shared between Desktop Popover and Mobile Drawer)
  const renderFilterPillsBar = () => (
    enableFilters && isLocal && availableStatuses.length > 0 ? (
      <Box
        sx={{
          px: 1.5,
          py: 0.75,
          display: 'flex',
          alignItems: 'center',
          gap: 0.75,
          overflowX: 'auto',
          scrollBehavior: 'smooth',
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
          borderBottom: '1px solid',
          borderColor: 'grey.100',
          bgcolor: 'grey.50',
          maskImage: 'linear-gradient(to right, black 90%, transparent 100%)',
          WebkitMaskImage: 'linear-gradient(to right, black 90%, transparent 100%)',
        }}
      >
        {/* "All" Pill */}
        <Chip
          label="All"
          size="small"
          onClick={() => handleFilterToggle('ALL')}
          sx={{
            height: 24,
            fontSize: '0.725rem',
            fontWeight: statusFilter.length === 0 ? 600 : 500,
            bgcolor: statusFilter.length === 0 ? 'primary.main' : 'background.paper',
            color: statusFilter.length === 0 ? 'primary.contrastText' : 'text.secondary',
            border: '1px solid',
            borderColor: statusFilter.length === 0 ? 'primary.main' : 'grey.200',
            cursor: 'pointer',
            flexShrink: 0,
            transition: 'all 0.15s ease',
            '&:hover': {
              bgcolor: statusFilter.length === 0 ? 'primary.dark' : 'grey.100',
            },
          }}
        />

        {/* Dynamic Status Pills */}
        {sortedStatuses.map((status) => {
          const isActive = statusFilter.includes(status);
          return (
            <Chip
              key={status}
              label={status}
              size="small"
              onClick={() => handleFilterToggle(status)}
              sx={{
                height: 24,
                fontSize: '0.725rem',
                fontWeight: isActive ? 600 : 500,
                bgcolor: isActive ? 'primary.main' : 'background.paper',
                color: isActive ? 'primary.contrastText' : 'text.secondary',
                border: '1px solid',
                borderColor: isActive ? 'primary.main' : 'grey.200',
                cursor: 'pointer',
                flexShrink: 0,
                transition: 'all 0.15s ease',
                '&:hover': {
                  bgcolor: isActive ? 'primary.dark' : 'grey.100',
                },
              }}
            />
          );
        })}
      </Box>
    ) : null
  );

  // Template List Items Container (Shared between Desktop Popover and Mobile Drawer)
  const renderTemplateList = () => (
    <>
      <Box
        sx={{
          overflowY: 'auto',
          flex: 1,
          minHeight: 0,
          height: isMobile ? '100%' : 240,
          maxHeight: isMobile ? 'none' : 240,
          '&::-webkit-scrollbar': { width: 5 },
          '&::-webkit-scrollbar-thumb': {
            bgcolor: 'grey.300',
            borderRadius: 3,
            '&:hover': { bgcolor: 'grey.400' },
          },
        }}
      >
        {isLoading && templatesToRender.length === 0 ? (
          <DropdownSkeleton />
        ) : visibleTemplates.length === 0 ? (
          /* Empty State - Perfectly Centered */
          <Box
            sx={{
              height: '100%',
              minHeight: 180,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: isMobile ? 'start' : 'center',
              py: 4,
              px: 2,
              textAlign: 'center',
            }}
          >
            <DescriptionOutlinedIcon sx={{ fontSize: 36, color: 'grey.300', mb: 1 }} />
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
              {searchText ? 'No templates match your search' : 'No templates available'}
            </Typography>
            {searchText && (
              <Typography variant="caption" color="text.disabled" sx={{ mt: 0.5, display: 'block' }}>
                Try searching by different keyword or filter
              </Typography>
            )}
          </Box>
        ) : (
          /* Template List */
          <Box sx={{ py: 0.5 }}>
            {visibleTemplates.map((template) => {
              const tid = getId(template);
              const isSelected = tid === value;
              const isUnavailable = isLocal
                ? unavailableTemplateIds.includes(tid) && tid !== value
                : false;
              const status = getStatus(template);

              return (
                <Box
                  key={tid}
                  onClick={() => !isUnavailable && handleSelect(template)}
                  sx={{
                    px: 2,
                    py: 1.1,
                    mx: 0.5,
                    my: 0.2,
                    borderRadius: 1,
                    minHeight: isMobile ? 48 : 'auto',
                    cursor: isUnavailable ? 'not-allowed' : 'pointer',
                    opacity: isUnavailable ? 0.45 : 1,
                    bgcolor: isSelected ? 'rgba(59, 130, 246, 0.08)' : 'transparent',
                    transition: 'background-color 0.12s ease',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1.5,
                    '&:hover': {
                      bgcolor: isUnavailable
                        ? 'transparent'
                        : (isSelected ? 'rgba(59, 130, 246, 0.12)' : 'grey.50'),
                    },
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: isSelected ? 600 : 500,
                        color: isSelected ? 'primary.dark' : 'text.primary',
                        fontSize: isMobile ? '0.9rem' : '0.84rem',
                        lineHeight: 1.35,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {template.name}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.3 }}>
                      <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.7rem' }}>
                        {template.language}
                      </Typography>
                      <Box sx={{ width: 3, height: 3, borderRadius: '50%', bgcolor: 'grey.300', flexShrink: 0 }} />
                      <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.7rem' }}>
                        {template.category}
                      </Typography>
                      {status && (
                        <>
                          <Box sx={{ width: 3, height: 3, borderRadius: '50%', bgcolor: 'grey.300', flexShrink: 0 }} />
                          <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem', fontWeight: 500 }}>
                            {status}
                          </Typography>
                        </>
                      )}
                    </Box>
                  </Box>

                  {/* Right Status Badge or Checkmark */}
                  {isSelected ? (
                    <CheckIcon sx={{ fontSize: 18, color: 'primary.main', flexShrink: 0 }} />
                  ) : isUnavailable ? (
                    <Chip
                      label="In use"
                      size="small"
                      sx={{
                        height: 18,
                        fontSize: '0.65rem',
                        fontWeight: 600,
                        bgcolor: 'rgba(107, 114, 128, 0.08)',
                        color: 'text.disabled',
                        flexShrink: 0,
                      }}
                    />
                  ) : null}
                </Box>
              );
            })}
          </Box>
        )}
      </Box>

      {/* Footer Bar */}
      <Box
        sx={{
          px: 2,
          py: 0.75,
          borderTop: '1px solid',
          borderColor: 'grey.100',
          bgcolor: 'grey.50',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-start',
        }}
      >
        <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.68rem', fontWeight: 500 }}>
          {visibleTemplates.length} template{visibleTemplates.length !== 1 ? 's' : ''}
        </Typography>
      </Box>
    </>
  );

  return (
    <ClickAwayListener onClickAway={handleClickAway} mouseEvent="onMouseDown" touchEvent="onTouchStart">
      <Box
        ref={anchorRef}
        sx={{
          position: 'relative',
          width: '100%',
          opacity: disabled ? 0.6 : 1,
          pointerEvents: disabled ? 'none' : 'auto',
        }}
      >
        {/* Unified Direct Type-to-Search Select Input */}
        <FormControl fullWidth required={required} error={error} disabled={disabled}>
          <TextField
            inputRef={mainInputRef}
            label={label}
            variant="outlined"
            value={open ? searchText : (valueObj?.name ?? '')}
            onChange={(e) => setSearchText(e.target.value)}
            onClick={handleToggle}
            onKeyDown={handleKeyDown}
            error={error}
            disabled={disabled}
            placeholder={open ? 'Search templates by name, type or language...' : label}
            InputLabelProps={{ shrink: open || !!valueObj || !!searchText }}
            inputProps={{
              autoComplete: 'off',
              autoCapitalize: 'none',
              autoCorrect: 'off',
              spellCheck: 'false',
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                transition: 'border-color 0.2s, box-shadow 0.2s',
                cursor: disabled ? 'default' : 'pointer',
                pr: 0.5,
                '&.Mui-focused': {
                  boxShadow: error
                    ? '0 0 0 3px rgba(239, 68, 68, 0.12)'
                    : '0 0 0 3px rgba(59, 130, 246, 0.12)',
                },
              },
              '& .MuiOutlinedInput-input': {
                cursor: open ? 'text' : (disabled ? 'default' : 'pointer'),
                textOverflow: 'ellipsis',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                userSelect: open ? 'auto' : 'none',
                minWidth: 0,
                width: 0,
                flex: '1 1 0%',
                pr: 1,
              },
            }}
            InputProps={{
              startAdornment: open ? (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: 'primary.main', fontSize: 18, mr: -0.5 }} />
                </InputAdornment>
              ) : undefined,
              endAdornment: (
                <InputAdornment position="end" sx={{ gap: 0.25, mr: 0.5, flexShrink: 0 }}>
                  {isLoading && (
                    <CircularProgress size={16} sx={{ mr: 0.5, color: 'primary.main' }} />
                  )}

                  {isLocal && Boolean(value) && (
                    <Tooltip
                      title={verifying ? 'This may take some time...' : 'Refresh Template'}
                      placement="top"
                    >
                      <Box component="span">
                        <IconButton
                          size="small"
                          onClick={(e) => { e.stopPropagation(); handleVerify(); }}
                          disabled={disabled || verifying}
                          sx={{
                            color: verifying ? 'primary.main' : 'action.active',
                            p: 0.5,
                            '&:hover': { bgcolor: 'rgba(59, 130, 246, 0.08)' },
                          }}
                        >
                          {verifying ? (
                            <CircularProgress size={14} />
                          ) : (
                            <SyncIcon sx={{ fontSize: 18 }} />
                          )}
                        </IconButton>
                      </Box>
                    </Tooltip>
                  )}

                  {(searchText || (value && !disabled)) && (
                    <Tooltip title={searchText ? 'Clear search' : 'Clear selection'} placement="top">
                      <IconButton
                        size="small"
                        onClick={handleClear}
                        sx={{
                          color: 'action.active',
                          p: 0.5,
                          '&:hover': { color: 'error.main', bgcolor: 'rgba(239, 68, 68, 0.08)' },
                        }}
                      >
                        <CloseIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                  )}

                  <IconButton
                    size="small"
                    onClick={handleToggle}
                    disabled={disabled}
                    sx={{
                      color: 'action.active',
                      p: 0.5,
                      transition: 'transform 0.2s',
                      transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
                      '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' },
                    }}
                  >
                    <KeyboardArrowDownRoundedIcon sx={{ fontSize: 20 }} />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          {helperText && <FormHelperText>{helperText}</FormHelperText>}
        </FormControl>

        {/* Verify result */}
        {verifyMessage && (
          <Fade in>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5, px: 0.5 }}>
              {verifyResult?.can_proceed
                ? <CheckCircleOutlineIcon sx={{ fontSize: 14, color: verifyColor }} />
                : <ErrorOutlineIcon sx={{ fontSize: 14, color: verifyColor }} />
              }
              <Typography variant="caption" sx={{ color: verifyColor }}>{verifyMessage}</Typography>
            </Box>
          </Fade>
        )}

        {/* ===================== MOBILE SWIPEABLE BOTTOM SHEET DRAWER ===================== */}
        {isMobile ? (
          <SwipeableDrawer
            anchor="bottom"
            open={open}
            onOpen={handleOpen}
            onClose={handleClose}
            swipeAreaWidth={30}
            disableBackdropTransition={false}
            PaperProps={{
              sx: {
                borderTopLeftRadius: 16,
                borderTopRightRadius: 16,
                height: '92vh',
                maxHeight: '92vh',
                display: 'flex',
                flexDirection: 'column',
                boxShadow: '0 -4px 24px rgba(0,0,0,0.18)',
              },
            }}
          >
            {/* Drawer Drag Handle */}
            <Box
              onClick={handleClose}
              sx={{
                width: '100%',
                display: 'flex',
                justifyContent: 'center',
                pt: 1.5,
                pb: 1,
                cursor: 'pointer',
                userSelect: 'none',
              }}
            >
              <Box sx={{ width: 42, height: 5, borderRadius: 2.5, bgcolor: 'grey.300' }} />
            </Box>

            {/* Mobile Header Search Input */}
            <Box sx={{ px: 2, pb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
              <TextField
                inputRef={mobileSearchRef}
                variant="outlined"
                fullWidth
                size="small"
                placeholder="Search templates by name..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                inputProps={{
                  enterKeyHint: 'search',
                  autoCapitalize: 'none',
                  autoCorrect: 'off',
                  autoComplete: 'off',
                  spellCheck: 'false',
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: 'primary.main', fontSize: 18 }} />
                    </InputAdornment>
                  ),
                  endAdornment: searchText ? (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setSearchText('')} sx={{ p: 0.25 }}>
                        <CloseIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </InputAdornment>
                  ) : undefined,
                }}
              />
              <IconButton size="small" onClick={handleClose}>
                <CloseIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Box>

            {renderFilterPillsBar()}
            {renderTemplateList()}
          </SwipeableDrawer>
        ) : (
          /* ===================== DESKTOP COMMAND PALETTE POPPER ===================== */
          <Popper
            open={open}
            anchorEl={anchorRef.current}
            placement="bottom-start"
            transition
            style={{
              width: anchorRef.current?.clientWidth ?? 'auto',
              minWidth: 320,
              zIndex: 1300,
            }}
            modifiers={[
              { name: 'offset', options: { offset: [0, 4] } },
              {
                name: 'preventOverflow',
                options: {
                  boundary: 'viewport',
                  padding: 12,
                },
              },
              {
                name: 'flip',
                enabled: false,
              },
            ]}
          >
            {({ TransitionProps }) => (
              <Fade {...TransitionProps} timeout={120}>
                <Paper
                  elevation={0}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    border: '1px solid',
                    borderColor: 'grey.200',
                    borderRadius: 2,
                    boxShadow: '0 12px 36px -8px rgba(0,0,0,0.15), 0 4px 12px -2px rgba(0,0,0,0.06)',
                    overflow: 'hidden',
                  }}
                >
                  {renderFilterPillsBar()}
                  {renderTemplateList()}
                </Paper>
              </Fade>
            )}
          </Popper>
        )}

        {/* Empty state message (when field has no options at all) */}
        {!open && templatesToRender.length === 0 && !loading && (hasExternalTemplates || hasFetched) && smbId && !disabled && (
          <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block', px: 1 }}>
            {isLocal
              ? ''
              : 'No approved Meta templates found for this business.'}
          </Typography>
        )}
      </Box>
    </ClickAwayListener>
  );
};

export default TemplateSelector;
